import {
  GetDocumentHistoriesRequest,
  GetDocumentHistoriesResponse,
  GetDocumentOverrideRequest,
  GetDocumentOverrideResponse,
  GetDocumentPreviewRequest,
  GetDocumentPreviewResponse,
  PatrickServiceClient,
  SubmitDocumentOverrideRequest,
  SubmitDocumentOverrideResponse,
} from '@policyfly/protobuf/patrick'

import { devtools } from '@/plugins/devtools/api'

import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'

export interface DocReviewApiEndpoints {
  /**
   * Retrieves preview eligibility and version histories for the requested attachments.
   */
  list: (attachmentIds: string[]) => Promise<GetDocumentHistoriesResponse>
  /**
   * Retrieves the piece-level document preview, optionally rebuilding it.
   */
  preview: (attachmentId: string, refresh?: boolean) => Promise<GetDocumentPreviewResponse>
  /**
   * Submits document overrides and starts generating a new version.
   */
  save: (request: SubmitDocumentOverrideRequest) => Promise<SubmitDocumentOverrideResponse>
  /**
   * Retrieves override run for a document preview.
   */
  run: (runId: string) => Promise<GetDocumentOverrideResponse>
}

export const createDocReviewEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<DocReviewApiEndpoints> => {
  const patrickServiceClient = new PatrickServiceClient(params.transport)

  const listDjango: DocReviewApiEndpoints['list'] = async () => {
    throw new Error('Document review is not supported on Django')
  }

  const previewDjango: DocReviewApiEndpoints['preview'] = async () => {
    throw new Error('Document review is not supported on Django')
  }

  const saveDjango: DocReviewApiEndpoints['save'] = async () => {
    throw new Error('Document review is not supported on Django')
  }

  const runDjango: DocReviewApiEndpoints['run'] = async () => {
    throw new Error('Document review is not supported on Django')
  }

  const listGrpc: DocReviewApiEndpoints['list'] = async (attachmentIds) => {
    const request = GetDocumentHistoriesRequest.create({ attachmentIds })
    const { response } = await patrickServiceClient.getDocumentHistories(request)

    devtools.logGrpc({
      description: 'Get Document Histories',
      messages: [
        { type: GetDocumentHistoriesRequest, key: 'request', message: request },
        { type: GetDocumentHistoriesResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const previewGrpc: DocReviewApiEndpoints['preview'] = async (attachmentId, refresh = false) => {
    const request = GetDocumentPreviewRequest.create({ attachmentId, refresh })
    const { response } = await patrickServiceClient.getDocumentPreview(request)

    devtools.logGrpc({
      description: 'Get Document Preview',
      messages: [
        { type: GetDocumentPreviewRequest, key: 'request', message: request },
        { type: GetDocumentPreviewResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const saveGrpc: DocReviewApiEndpoints['save'] = async (request) => {
    const { response } = await patrickServiceClient.submitDocumentOverride(request)

    devtools.logGrpc({
      description: 'Submit Document Override',
      messages: [
        { type: SubmitDocumentOverrideRequest, key: 'request', message: request },
        { type: SubmitDocumentOverrideResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  const runGrpc: DocReviewApiEndpoints['run'] = async (runId) => {
    const request = GetDocumentOverrideRequest.create({ runId })
    const { response } = await patrickServiceClient.getDocumentOverride(request)

    devtools.logGrpc({
      description: 'Get Document Override',
      messages: [
        { type: GetDocumentOverrideRequest, key: 'request', message: request },
        { type: GetDocumentOverrideResponse, key: 'response', message: response },
      ],
    })

    return response
  }

  return {
    django: {
      list: listDjango,
      preview: previewDjango,
      save: saveDjango,
      run: runDjango,
    },
    grpc: {
      list: listGrpc,
      preview: previewGrpc,
      save: saveGrpc,
      run: runGrpc,
    },
  }
}
